import { FormEvent, StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import type { Session } from "@supabase/supabase-js";
import { supabase, supabaseConfigured } from "./lib/supabase";
import { Dashboard, type DashboardMembership, type DashboardProfile } from "./Dashboard";
import type { SupportedCountry, SupportedCurrency, SupportedLocale } from "./localization";
import "./design/tokens.css";
import "./styles.css";
import "./crm.css";
import "./crm-operations.css";

type Profile = {
  user_id: string;
  display_name: string | null;
  password_change_required: boolean;
  first_access_completed_at: string | null;
  locale: SupportedLocale;
  country_code: SupportedCountry;
  currency_code: SupportedCurrency;
  timezone: string;
};

type PlatformMembership = {
  role_key: "super_master" | "platform_master" | "commercial_manager" | "commercial";
  permissions: string[];
  active: boolean;
};

const environment = import.meta.env.VITE_APP_ENV ?? "local";

function Brand() {
  return <div className="brand"><span className="brand-mark" aria-hidden="true"><i /></span><span><strong>Vorno</strong><small>Drive</small></span></div>;
}

function LoginScreen({ onAuthenticated }: { onAuthenticated: (session: Session) => void }) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabaseConfigured) return setError("O ambiente ainda não está ligado ao serviço de autenticação.");
    const normalized = identifier.trim().toLowerCase();
    if (!normalized || !password) return setError("Preencha o utilizador e a palavra-passe.");
    setLoading(true);
    setError(null);
    try {
      let email = normalized;
      if (!normalized.includes("@")) {
        const { data, error: aliasError } = await supabase.rpc("resolve_login_alias", { requested_alias: normalized });
        if (aliasError || !data) throw new Error("Utilizador ou palavra-passe inválidos.");
        email = String(data);
      }
      const { data, error: loginError } = await supabase.auth.signInWithPassword({ email, password });
      if (loginError || !data.session) throw new Error("Utilizador ou palavra-passe inválidos.");
      onAuthenticated(data.session);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível iniciar sessão.");
    } finally {
      setLoading(false);
    }
  };

  return <main className="auth-page">
    <section className="auth-story">
      <Brand />
      <div className="story-copy"><p className="eyebrow">Universo Vorno · Gestão automóvel</p><h1>Todo o stand.<br/><em>Uma única direção.</em></h1><p>Stock, equipa, clientes e operação numa plataforma concebida para acompanhar cada viatura do primeiro registo à entrega.</p></div>
      <div className="journey" aria-hidden="true"><i/><span>Entrada</span><i/><span>Preparação</span><i/><span>Entrega</span></div>
      <small className="environment-label">Ambiente {environment}</small>
    </section>
    <section className="auth-panel">
      <div className="auth-card">
        <div className="mobile-brand"><Brand /></div>
        <p className="eyebrow">Acesso protegido</p><h2>Bem-vindo de volta.</h2><p className="auth-intro">Introduza as suas credenciais para entrar no Vorno Drive.</p>
        <form onSubmit={submit}>
          <label><span>Utilizador</span><input autoComplete="username" autoFocus value={identifier} onChange={(e)=>setIdentifier(e.target.value)} placeholder="O seu utilizador" /></label>
          <label><span>Palavra-passe</span><span className="password-field"><input autoComplete="current-password" type={passwordVisible?"text":"password"} value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="A sua palavra-passe"/><button aria-label={passwordVisible?"Ocultar palavra-passe":"Mostrar palavra-passe"} onClick={()=>setPasswordVisible(v=>!v)} type="button">{passwordVisible?"Ocultar":"Mostrar"}</button></span></label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button" disabled={loading} type="submit">{loading?"A validar…":"Entrar no Vorno Drive"}</button>
        </form>
        <p className="auth-note">O acesso é atribuído pela administração da plataforma ou pelo Master do seu stand.</p>
      </div>
    </section>
  </main>;
}

function PasswordChangeScreen({ onCompleted, onSignOut }: { onCompleted: ()=>void; onSignOut: ()=>void }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string|null>(null);
  const [saving, setSaving] = useState(false);
  const valid = password.length >= 10 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password) && /[^A-Za-z0-9]/.test(password);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return setError("Use pelo menos 10 caracteres, incluindo maiúscula, minúscula, número e símbolo.");
    if (password !== confirmation) return setError("As palavras-passe não coincidem.");
    setSaving(true); setError(null);
    const { error: passwordError } = await supabase.auth.updateUser({ password });
    if (passwordError) { setSaving(false); return setError(passwordError.message); }
    const { data, error: completeError } = await supabase.rpc("complete_password_change");
    if (completeError || data !== true) { setSaving(false); return setError("A senha foi atualizada, mas o primeiro acesso não pôde ser concluído. Entre novamente."); }
    setSaving(false); onCompleted();
  };

  return <main className="first-access-page"><section className="first-access-card"><Brand/><div className="step-badge">Primeiro acesso · obrigatório</div><h1>Proteja o seu acesso Master.</h1><p>A palavra-passe recebida é temporária. Defina agora uma nova palavra-passe pessoal antes de entrar na administração.</p><form onSubmit={submit}><label><span>Nova palavra-passe</span><input autoComplete="new-password" autoFocus type="password" value={password} onChange={(e)=>setPassword(e.target.value)}/></label><label><span>Confirmar nova palavra-passe</span><input autoComplete="new-password" type="password" value={confirmation} onChange={(e)=>setConfirmation(e.target.value)}/></label><div className={`password-rule ${valid?"valid":""}`}><i/>{valid?"A palavra-passe cumpre os requisitos.":"10+ caracteres, maiúscula, minúscula, número e símbolo."}</div>{error&&<p className="form-error" role="alert">{error}</p>}<button className="primary-button" disabled={saving} type="submit">{saving?"A proteger acesso…":"Definir nova palavra-passe"}</button><button className="text-button" onClick={onSignOut} type="button">Sair e concluir mais tarde</button></form></section></main>;
}

function App() {
  const [session, setSession] = useState<Session|null>(null);
  const [profile, setProfile] = useState<Profile|null>(null);
  const [membership, setMembership] = useState<PlatformMembership|null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string|null>(null);

  const loadAccess = async (activeSession: Session|null) => {
    setSession(activeSession); setProfile(null); setMembership(null); setLoadError(null);
    if (!activeSession) { setLoading(false); return; }
    setLoading(true);
    const [{data:profileData,error:profileError},{data:membershipData,error:membershipError}] = await Promise.all([
      supabase.from("profiles").select("user_id,display_name,password_change_required,first_access_completed_at,locale,country_code,currency_code,timezone").eq("user_id",activeSession.user.id).single(),
      supabase.from("platform_memberships").select("role_key,permissions,active").eq("user_id",activeSession.user.id).single(),
    ]);
    if (profileError || membershipError || !profileData || !membershipData) setLoadError("Este acesso ainda não possui autorização administrativa válida.");
    else { setProfile(profileData as Profile); setMembership(membershipData as PlatformMembership); }
    setLoading(false);
  };

  useEffect(()=>{ void supabase.auth.getSession().then(({data})=>loadAccess(data.session)); const {data:{subscription}}=supabase.auth.onAuthStateChange((_event,next)=>{setSession(next); if(!next){setProfile(null);setMembership(null);}}); return ()=>subscription.unsubscribe(); },[]);
  const signOut = async()=>{ await supabase.auth.signOut(); await loadAccess(null); };
  if (loading) return <main className="loading-page"><Brand/><span className="loader"/><p>A preparar o Vorno Drive…</p></main>;
  if (!session) return <LoginScreen onAuthenticated={loadAccess}/>;
  if (loadError || !profile || !membership) return <main className="loading-page"><Brand/><p className="form-error">{loadError??"Acesso não autorizado."}</p><button className="text-button" onClick={signOut}>Voltar ao login</button></main>;
  if (profile.password_change_required) return <PasswordChangeScreen onCompleted={()=>loadAccess(session)} onSignOut={signOut}/>;
  return <Dashboard profile={profile as DashboardProfile} membership={membership as DashboardMembership} onSignOut={signOut} onProfileUpdated={(updated)=>setProfile(current=>current?{...current,...updated}:current)}/>;
}

createRoot(document.getElementById("root")!).render(<StrictMode><App/></StrictMode>);
